var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
export var emptySpacesOrchestrationState = {
    pendingSpaceSwap: null,
    pendingOrganizerMove: null,
};
/**
 * The identity a space can only get from the server snapshot: its invitee
 * row and organizer role. A space re-added through any door (sider, grid
 * patch, search) arrives as a fresh object without these — losing them
 * would, e.g., let an organizer space be removed as if it were ordinary.
 */
var rehydrateIdentity = function (space, initialSpaces) {
    var snapshot = initialSpaces === null || initialSpaces === void 0 ? void 0 : initialSpaces.find(function (initial) { return initial.id === space.id; });
    var inviteeId = snapshot === null || snapshot === void 0 ? void 0 : snapshot.inviteeId;
    var isOrganizer = snapshot === null || snapshot === void 0 ? void 0 : snapshot.isOrganizer;
    if (space.inviteeId === inviteeId && space.isOrganizer === isOrganizer) {
        return space;
    }
    // Also STRIPS identity a space shouldn't have (not in the snapshot):
    // identity comes from the server, never from a writer's payload.
    return __assign(__assign({}, space), { inviteeId: inviteeId, isOrganizer: isOrganizer });
};
/**
 * Rules applied, in order:
 * 1. Dedupe — a space id appears at most once (first occurrence wins).
 * 2. Request-only — a managed space is mutually exclusive with all other
 *    spaces. Given an already-mixed list the first managed space wins,
 *    matching `reconcileSpacesByIds`'s full-list rule. (Add-time flows keep
 *    `resolveSpacesAfterAdd`'s incoming-wins rule — their output is already
 *    valid, so this backstop never rewrites it.)
 * 3. Identity rehydration — snapshot identity fields (`inviteeId`,
 *    `isOrganizer`) are re-applied from `initialSpaces` and stripped when
 *    the snapshot has no such space (always stripped in CREATE).
 */
export function normalizeSpaces(_a) {
    var _b;
    var spaces = _a.spaces, initialSpaces = _a.initialSpaces;
    var input = spaces !== null && spaces !== void 0 ? spaces : [];
    var seen = new Set();
    var deduped = input.filter(function (space) {
        if (seen.has(space.id))
            return false;
        seen.add(space.id);
        return true;
    });
    var managedSpace = deduped.find(function (space) { return space.isManaged; });
    // Organizer protection outranks request-only exclusivity: a managed space
    // can never evict the snapshot organizer, so when both are present the
    // managed space is dropped instead. A swap TO a managed space never
    // collides — the organizer has already left the input list by then.
    var organizerId = (_b = initialSpaces === null || initialSpaces === void 0 ? void 0 : initialSpaces.find(function (space) { return space.isOrganizer; })) === null || _b === void 0 ? void 0 : _b.id;
    var organizerPresent = organizerId !== undefined &&
        (managedSpace === null || managedSpace === void 0 ? void 0 : managedSpace.id) !== organizerId &&
        deduped.some(function (space) { return space.id === organizerId; });
    var requestOnlyResolved = managedSpace && deduped.length > 1
        ? organizerPresent
            ? deduped.filter(function (space) { return !space.isManaged; })
            : [managedSpace]
        : deduped;
    var identityChanged = false;
    var rehydrated = requestOnlyResolved.map(function (space) {
        var next = rehydrateIdentity(space, initialSpaces);
        if (next !== space)
            identityChanged = true;
        return next;
    });
    var resultIds = new Set(rehydrated.map(function (space) { return space.id; }));
    var droppedSpaceIds = __spreadArray([], __read(new Set(input.map(function (space) { return space.id; }).filter(function (id) { return !resultIds.has(id); }))), false);
    var changed = identityChanged || rehydrated.length !== input.length;
    return {
        spaces: changed ? rehydrated : input,
        droppedSpaceIds: droppedSpaceIds,
        changed: changed,
    };
}
/** True while this space is the one being replaced via "Change space". */
export function isSpaceChangePending(state, spaceId) {
    var _a;
    return ((_a = state.pendingSpaceSwap) === null || _a === void 0 ? void 0 : _a.spaceId) === spaceId;
}
/**
 * The space organizes the event. Snapshot-derived — a writer's payload can
 * never mint this (see rehydrateIdentity). Always false in CREATE.
 */
export function isOrganizerSpace(initialSpaces, spaceId) {
    return !!(initialSpaces === null || initialSpaces === void 0 ? void 0 : initialSpaces.some(function (space) { return space.isOrganizer && space.id === spaceId; }));
}
/**
 * State transition for a completed "change space" swap: the organizer
 * swapping out records a move; swapping the recorded target re-targets it —
 * or aborts it when the target is swapped back to the original organizer.
 * Any other swap leaves the pending move untouched.
 */
export function pendingMoveAfterSwap(pending, swappedOut, incoming) {
    if (swappedOut.isOrganizer) {
        return { fromSpaceId: swappedOut.id, toSpaceId: incoming.id };
    }
    if ((pending === null || pending === void 0 ? void 0 : pending.toSpaceId) === swappedOut.id) {
        return incoming.id === pending.fromSpaceId
            ? null
            : __assign(__assign({}, pending), { toSpaceId: incoming.id });
    }
    return pending;
}
/**
 * State transition for any spaces write: a pending move is aborted the
 * moment the original organizer is back in the form (through any door) —
 * no move will fire at save, so the recorded target must not keep its
 * protection. A later swap re-records.
 */
export function pendingMoveAfterSpacesChange(pending, spaces) {
    return pending && spaces.some(function (space) { return space.id === pending.fromSpaceId; })
        ? null
        : pending;
}
/**
 * Diff-at-save gate for the /move operation. A move fires only when:
 * - the event had an organizer space when edit opened,
 * - that space is no longer in the form (it can only leave via a swap —
 *   Remove is blocked for organizer spaces), and
 * - the swap's recorded replacement is still in the form.
 */
export function resolvePendingMove(state, initialSpaces, formSpaces) {
    var pending = state.pendingOrganizerMove;
    if (!pending)
        return null;
    var fromSpace = initialSpaces === null || initialSpaces === void 0 ? void 0 : initialSpaces.find(function (space) { return space.isOrganizer; });
    if (!fromSpace || fromSpace.id !== pending.fromSpaceId)
        return null;
    var spaces = formSpaces !== null && formSpaces !== void 0 ? formSpaces : [];
    if (spaces.some(function (space) { return space.id === fromSpace.id; }))
        return null;
    var toSpace = spaces.find(function (space) { return space.id === pending.toSpaceId; });
    if (!toSpace)
        return null;
    return { fromSpace: fromSpace, toSpace: toSpace };
}
/**
 * Remove-protection for a space card. The organizer space can never be
 * removed (that would cancel the event); the target of a move that would
 * fire is protected too — the event must keep an organizer space. The
 * target check is diff-aware: once the move resolves to "no move" (e.g.
 * the original organizer was re-added), the target is removable again.
 */
export function canRemoveSpace(state, initialSpaces, formSpaces, spaceId) {
    var _a;
    if (isOrganizerSpace(initialSpaces, spaceId)) {
        return { allowed: false, reason: 'organizer' };
    }
    if (((_a = resolvePendingMove(state, initialSpaces, formSpaces)) === null || _a === void 0 ? void 0 : _a.toSpace.id) === spaceId) {
        return { allowed: false, reason: 'move_target' };
    }
    return { allowed: true };
}
